import { profileSlugFromUrl } from '@/modules/profile/utils/profileSlugFromUrl';

/**
 * Search results → doctor profile: start the booking card's requests when the patient pauses on a
 * doctor's card, before the tap (docs/performance.md §۳.۱۸).
 *
 * A doctor link that stays at least 60% on screen for 1s counts as a pause. Cards already on the first
 * screen of a result set (before the patient scrolls) are prefetched at once: they are the ones most
 * often tapped right away (owner decision, 2026-09-30). Each doctor is
 * prefetched once, at most `DWELL_PREFETCH_CAP` doctors per result set (the caller restarts this
 * on a new search), never on Save-Data / 2G. What is fetched, and under which keys, is up to
 * `onDwell` (the same `prefetchBookingPieces` a tap uses), so the profile reads the prefetched
 * response — or waits for the same in-flight request — instead of sending its own.
 */

const DOCTOR_LINK_SELECTOR = 'a[href*="/dr/"]';
export const DWELL_MS = 1000;
export const DWELL_VISIBLE_RATIO = 0.6;
export const DWELL_PREFETCH_CAP = 6;

export const isConstrainedConnection = (): boolean => {
  const connection = (navigator as any)?.connection;
  if (!connection) return false;
  return connection.saveData === true || /(^|-)2g$/.test(String(connection.effectiveType ?? ''));
};

/** Decoded doctor slug of a same-origin profile link, else null. */
const slugOf = (anchor: HTMLAnchorElement): string | null => {
  try {
    const url = new URL(anchor.href, location.href);
    if (url.origin !== location.origin) return null;
    const slug = profileSlugFromUrl(url.pathname);
    return slug ? decodeURIComponent(slug) : null;
  } catch {
    return null;
  }
};

export function startBookingDwellPrefetch(root: HTMLElement, onDwell: (slug: string) => void): () => void {
  if (typeof IntersectionObserver === 'undefined' || typeof MutationObserver === 'undefined') return () => undefined;

  const done = new Set<string>();
  const timers = new Map<Element, ReturnType<typeof setTimeout>>();
  const observed = new WeakSet<Element>();
  const full = () => done.size >= DWELL_PREFETCH_CAP;
  // True until the first scroll of this result set: cards on screen until then need no pause.
  let firstScreen = true;
  const onScroll = () => {
    firstScreen = false;
    window.removeEventListener('scroll', onScroll);
  };

  const stop = () => {
    window.removeEventListener('scroll', onScroll);
    io.disconnect();
    mo.disconnect();
    timers.forEach(t => clearTimeout(t));
    timers.clear();
  };

  const fire = (anchor: HTMLAnchorElement) => {
    timers.delete(anchor);
    if (full() || isConstrainedConnection() || document.visibilityState === 'hidden') return;
    const slug = slugOf(anchor);
    if (!slug || done.has(slug)) return;
    done.add(slug);
    try {
      onDwell(slug);
    } catch {
      // Best-effort; a prefetch must never break the results list.
    }
    if (full()) stop();
  };

  const io = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        const anchor = entry.target as HTMLAnchorElement;
        if (entry.isIntersecting && entry.intersectionRatio >= DWELL_VISIBLE_RATIO) {
          if (!timers.has(anchor)) timers.set(anchor, setTimeout(() => fire(anchor), firstScreen ? 0 : DWELL_MS));
        } else {
          clearTimeout(timers.get(anchor));
          timers.delete(anchor);
        }
      }
    },
    { threshold: [0, DWELL_VISIBLE_RATIO] },
  );

  const scan = () => {
    root.querySelectorAll<HTMLAnchorElement>(DOCTOR_LINK_SELECTOR).forEach(anchor => {
      if (observed.has(anchor)) return;
      observed.add(anchor);
      io.observe(anchor);
    });
  };
  // Result cards render progressively and on "next page"; batch mutations into one scan per frame.
  let scanQueued = false;
  const mo = new MutationObserver(() => {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(() => {
      scanQueued = false;
      if (!full()) scan();
    });
  });

  if (isConstrainedConnection()) return () => undefined;
  window.addEventListener('scroll', onScroll, { passive: true });
  mo.observe(root, { childList: true, subtree: true });
  scan();
  return stop;
}
